import React, { useContext } from 'react';
import styled from 'styled-components/macro';

import { fontkoKR, fontDefault, fontDefaultAlt } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';

import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { FormattedInlineText, FormattedText } from '@mirinae/lessons/components/lesson/elements/Text';
import { lessonsPalette, colorMap } from '@mirinae/lessons/modules/defines/styles';

const SummaryWrapper = styled(Element)`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
`;

const SummaryFrame = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    border: 1px solid ${lessonsPalette.grey[5]};
    border-radius: 10px;
    margin-bottom: 20px;
    margin-top: 15px;
    width: ${os.isOthers ? 'fit-content' : '100%'};
`;

const Title = styled.div`
    background: ${lessonsPalette.light[2]};
    border-radius: 17px;
    text-align: center;
    margin-top: -17px;
    padding: 7px 20px;
    width: fit-content;
    font-family: ${fontDefaultAlt};
    font-style: normal;
    font-weight: bold;
    font-size: 12px;
    line-height: 15px;
    color: ${lessonsPalette.grey[2]};    
`;

const MeaningBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: max-content;
    text-align: center;
    ${props => (os.isOthers ? `
        max-width: ${props.player.currentPage.lessonBounds.width * 0.25}px;
        width: ${props.width}px; 
        margin: 20px 0 25px;
        padding: 0 20px;
    ` : `
        margin: 10px 0px;
        padding: 0px 15px;
        width: auto;
    `)}
`;

const Boxes = styled.div`
    display: flex;

    ${os.isOthers ? `
        flex-direction: row;
        ${MeaningBox}:not(:last-child) {
            border-right: 1px solid ${lessonsPalette.grey[5]};
        }
    ` : `
        flex-direction: column;
        width: calc(100% - 40px);
        ${MeaningBox}:not(:last-child) {
            border-bottom: 1px solid ${lessonsPalette.grey[5]};
            padding-bottom: 20px;
        }
    `}
`;

const BoxTitle = styled.div`
    font-family: ${fontDefault};
    font-weight: 700;
    font-size: 14px;
    line-height: 21px;
    text-align: center;
    color: ${lessonsPalette.darken[1]};
    margin-bottom: 10px;
`;

const Meaning = styled.div`
    font-family: ${fontDefault};
    font-weight: 400;
    font-size: 14px;
    line-height: 150%;
    color: ${lessonsPalette.primary.black};
`;

const CombinesWithBox = styled(MeaningBox)`
`;

const BoxList = styled.ul`
`;

const ListItem = styled.li`
    list-style-type: none;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: ${lessonsPalette.grey[0]};
    // &::before {    // dropping the bullet seems better for patterns as they usually start with a '-' 
    //     content: '';
    //     display: inline-block;
    //     vertical-align: middle;
    //     margin: 0 4px 0 0;
    //     width: 2px;
    //     height: 2px;
    //     border-radius: 50%;
    //     background-color: ${lessonsPalette.grey[0]};
    }
`;

const Combiner = styled(ListItem)`
    font-family: ${fontDefault};
`;

const VariantsBox = styled(MeaningBox)`
    // border: none;
`;

const Variant = styled(ListItem)`
    font-family: ${fontkoKR};
    color: ${props => colorMap[props.part.color || 'pattern']};
    font-weight: 400;
    font-size: 16px;
    line-height: 25px;
`;

const PatternVariantsSummary = ({ element }) => {
    const player = useContext(PlayerContext);

    return (
        <SummaryWrapper element={element}>
            <SummaryFrame>
                <Title>{element.fieldsByName.title.value}</Title>
                <Boxes>
                    <MeaningBox player={player} width={element.geom.width / 3}>
                        <BoxTitle>Meaning</BoxTitle>
                        <Meaning>
                            <FormattedText text={element.fieldsByName.meaning.value} styles={element.fieldsByName.meaning.styles} links={element.fieldsByName.meaning.links} />
                        </Meaning>
                    </MeaningBox>
                    <CombinesWithBox player={player} width={element.geom.width / 3}>
                        <BoxTitle>Combines with</BoxTitle>
                        <BoxList>
                            { element.fieldsByName.combinesWith.parts.englishParts.map((p, i) => (
                                <Combiner type="circle" key={i} part={p}>
                                    <FormattedInlineText text={p.value} />
                                </Combiner>
                            ))}
                        </BoxList>
                    </CombinesWithBox>
                    <VariantsBox player={player} width={element.geom.width / 3}>
                        <BoxTitle>Variants</BoxTitle>
                        <BoxList>
                            { element.fieldsByName.variants.parts.koreanParts.map((p, i) => (
                                <Variant type="circle" key={i} part={p}>
                                    {p.value}
                                </Variant>
                            ))}
                        </BoxList>
                    </VariantsBox>
                </Boxes>
            </SummaryFrame>
        </SummaryWrapper>
    );
};

export default PatternVariantsSummary;
